import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import axios from '../api';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getUserSettings, updateUserSettings } from '../settings';
import { useDebounce } from '../hooks/useDebounce';
import TrialSearchFilters from './TrialSearchFilters';
import { CO_LABEL } from '../utils/contractLabel';
import TrialStatusBadge from './TrialStatusBadge';
import {
  EMPTY_TRIAL_FILTERS,
  TEXT_FILTER_KEYS,
  activeFilters,
  filtersFromSearchParams,
  hasActiveFilters,
} from '../utils/trialSearchFilters';

/** The filters and "Show closed trials" saved for the user under `key` ({ filters, closed }), or null. */
function savedTrialFilters(key) {
  const saved = key ? getUserSettings()[key] : null;
  return saved && typeof saved === 'object' ? { filters: { ...EMPTY_TRIAL_FILTERS, ...activeFilters(saved.filters ?? {}) }, closed: Boolean(saved.closed) } : null;
}

// Get current user ID from localStorage (adjust if you use context or props)
const getCurrentUserId = () => {
  try {
    const userRaw = sessionStorage.getItem('userInfo');
    if (!userRaw) return null;
    let user;
    try {
      user = JSON.parse(userRaw);
    } catch {
      user = userRaw;
    }
    const userData = user?.data ?? user;
    return userData?.id ?? userData?.userId ?? userData?.userid ?? null;
  } catch (e) {
    return null;
  }
};

// Columns the API can sort on (sort_by keys in TrialController::index)
const SORTABLE_COLUMNS = [
  'protocolno', 'trialno', 'contract_no', 'customer', 'studydirector',
  'customer_no', 'year', 'country', 'host', 'crop', 'study_type', 'location', 'test_item',
];

export default function TrialsTable({
  // Configuration props
  showFilters = true,
  showProtocolFilter = true,
  showCustomerFilter = true,
  showStudyDirectorFilter = true,
  showClosedToggle = true,
  showColumnConfig = true,
  showMassUpdate = false,
  showActions = true,
  showSearchFilters = false, // full search panel (replaces the protocol/customer/SD inputs)
  syncFiltersToUrl = false,  // keep the search panel's filters in the URL query string
  // Remember the search panel's filters and "Show closed trials" for the user under this settings key (customer
  // point 64): opened without filters in the URL, the saved ones are used; filters in the URL always win (links,
  // Back). Saved only when the user changes them; Clear clears them.
  rememberFiltersAs = null,
  compactSearchFilters = false, // search panel shows search/year/customer, the rest behind "More filters"
  // Filters set outside the table (the dashboard's filter panel): the active trial filters, only
  // the user's trials (mine), and/or only these trial ids. null = not set.
  externalFilters = null,
  mineOnly = false,
  onlyIds = null,
  // Trial ids in the order to list them first (the dashboard's: most urgent task first); the table
  // starts sorted that way until a column header is clicked. null = sort by Protocol No.
  priorityIds = null,

  // Auto-filter props
  autoFilterPI = null,
  autoFilterFT = null,
  autoFilterSD = null,
  autoFilterCustomer = null,
  autoFilterProtocol = null,

  // Column configuration
  defaultColumns = ['trialno', 'contract_no', 'customer', 'studydirector', 'PI', 'FT', 'status'],
  columnStorageKey = 'trialsTableColumns',

  // Pagination
  defaultPerPage = 15,
  perPageStorageKey = 'trialsTablePerPage',

  // Callbacks
  onTrialSelect = null,
  rowClickSelects = false, // with massUpdateMode: clicking a row ticks its checkbox
  onTrialsChange = null,
  onResultsChange = null, // ({ total, query }) after each load; query = the filters without pagination
  onRefresh = null, // NEW: Callback to expose refresh function

  // Styling
  className = '',
  tableClassName = '',

  // Mass update integration
  massUpdateMode = false,
  selectedTrialIds = [],
  onSelectionChange = null,
  
  // NEW: Refresh trigger
  refreshTrigger = 0,
}) {
  const navigate = useNavigate();
  // A trial opened from this table (View / Edit) goes Back here, with the filters
  const location = useLocation();
  const backHere = { from: location.pathname + location.search };
  const [searchParams, setSearchParams] = useSearchParams();

  // Search panel filters. Text fields are debounced before they reach the API.
  const [searchFilters, setSearchFilters] = useState(() => {
    if (!syncFiltersToUrl) return EMPTY_TRIAL_FILTERS;
    const fromUrl = filtersFromSearchParams(searchParams);
    return hasActiveFilters(fromUrl) ? fromUrl : savedTrialFilters(rememberFiltersAs)?.filters ?? fromUrl;
  });
  // Whether the user has changed the filters here: only then are they saved, and a late load of the saved ones
  // (the settings arrive after the page) no longer replaces them
  const filtersTouched = useRef(false);
  const urlHadFilters = useRef(syncFiltersToUrl && hasActiveFilters(filtersFromSearchParams(searchParams)));
  const debouncedText = useDebounce(JSON.stringify(TEXT_FILTER_KEYS.map(k => searchFilters[k])), 400);
  const nonTextFilters = JSON.stringify(
    Object.fromEntries(Object.entries(searchFilters).filter(([k]) => !TEXT_FILTER_KEYS.includes(k)))
  );
  // Stable string of the filters to query with, so it can be a plain hook dependency
  const searchQueryKey = useMemo(() => {
    const text = JSON.parse(debouncedText);
    const textFilters = Object.fromEntries(TEXT_FILTER_KEYS.map((k, i) => [k, text[i]]));
    return JSON.stringify(activeFilters({ ...JSON.parse(nonTextFilters), ...textFilters }));
  }, [debouncedText, nonTextFilters]);
  const latestRequestRef = useRef(0);
  const externalKey = JSON.stringify({ externalFilters, mineOnly, onlyIds });

  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  // Other filters from outside: back to the first page
  useEffect(() => { setPage(1); }, [externalKey]);
  const [meta, setMeta] = useState(null);
  const [showClosed, setShowClosed] = useState(() => savedTrialFilters(rememberFiltersAs)?.closed ?? false);
  const [filters, setFilters] = useState({
    protocolno: autoFilterProtocol || '',
    customer: autoFilterCustomer || '',
    studydirector: ''
  });
  const [users, setUsers] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [studyDirectorInput, setStudyDirectorInput] = useState('');
  const [filteredUserIds, setFilteredUserIds] = useState([]);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [sortBy, setSortBy] = useState(() => (Array.isArray(priorityIds) ? 'priority' : 'protocolno'));
  // The priority order as a plain string (no refetch for a new array with the same ids), only while sorted by it
  const priorityKey = sortBy === 'priority' ? JSON.stringify(priorityIds ?? []) : '';
  const [sortDirection, setSortDirection] = useState('asc');
  const [perPage, setPerPage] = useState(() => {
    const stored = sessionStorage.getItem(perPageStorageKey);
    if (stored === 'all') return 'all';
    const num = stored ? Number(stored) : defaultPerPage;
    return [10, 15, 25, 50, 100].includes(num) ? num : defaultPerPage;
  });
  const [customers, setCustomers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(getCurrentUserId());

  // Column configuration
  const [columns, setColumns] = useState(() => {
    try {
      const raw = sessionStorage.getItem(columnStorageKey);
      if (!raw) return defaultColumns;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
      return defaultColumns;
    } catch {
      return defaultColumns;
    }
  });
  const [showColumnConfigDropdown, setShowColumnConfigDropdown] = useState(false);
  const [savingColumns, setSavingColumns] = useState(false);
  const columnMenuRef = useRef(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    sessionStorage.setItem(perPageStorageKey, String(perPage));
    const t = setTimeout(() => {
      updateUserSettings({ [`${perPageStorageKey}_value`]: perPage });
    }, 300);
    return () => clearTimeout(t);
  }, [perPage, perPageStorageKey]);

  // Fetch customers and users on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await axios.post('/masterdata/customers', {});
        setCustomers(res.data || []);
      } catch {
        setCustomers([]);
      }
    };

    const fetchUsers = async () => {
      try {
        const res = await axios.post('/masterdata/users', {});
        const raw = res.data || [];
        const mapped = (Array.isArray(raw) ? raw : []).map(u => {
          const id = u?.id;
          const name = u?.name;
          const email = u?.email;
          return { id, name, email };
        }).filter(u => u.id != null && u.name);

        setUsers(mapped);
        const byId = {};
        for (const u of mapped) {
          byId[String(u.id)] = u;
        }
        setUsersById(byId);
      } catch {
        setUsers([]);
        setUsersById({});
      }
    };

    fetchCustomers();
    fetchUsers();
  }, []);

  // Auto-filter effect - set up filtered user IDs based on auto-filter props
  useEffect(() => {
    const autoFilteredIds = [];

    if (autoFilterSD) {
      autoFilteredIds.push(String(autoFilterSD));
    }

    if (autoFilteredIds.length > 0) {
      setFilteredUserIds(autoFilteredIds);
    }
  }, [autoFilterSD, autoFilterPI, autoFilterFT]);

  // Latest onResultsChange in a ref, so an inline callback from the parent doesn't refetch
  const onResultsChangeRef = useRef(onResultsChange);
  useEffect(() => { onResultsChangeRef.current = onResultsChange; }, [onResultsChange]);

  // Fetch trials function (make it accessible)
  const fetchTrials = useCallback(async () => {
    // Typing in the search fields can fire several requests; only the latest may update the table
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const paginateFlag = perPage !== 'all';
      const external = JSON.parse(externalKey);
      const payload = {
        closed: showClosed ? null : false,
        sort_by: sortBy,
        sort_direction: sortDirection,
        protocolno: filters.protocolno || null,
        customer: filters.customer || null,
        studydirector: filteredUserIds.length > 0 ? filteredUserIds : null,
        // OR'ed with studydirector by the API (see Trial::scopeFilter)
        ...(autoFilterPI && { pi: autoFilterPI }),
        ...(autoFilterFT && { ft: autoFilterFT }),
        ...(showSearchFilters ? JSON.parse(searchQueryKey) : {}),
        // (read from externalKey, so a new array/object with the same content does not refetch)
        ...external.externalFilters,
        ...(external.mineOnly ? { mine: true } : {}),
        ...(Array.isArray(external.onlyIds) ? { ids: external.onlyIds } : {}),
        ...(sortBy === 'priority' ? { priority_ids: JSON.parse(priorityKey) } : {}),
        ...(paginateFlag ? { paginate: true, per_page: perPage, page } : { paginate: false })
      };

      const res = await axios.post('/trials', payload);
      if (requestId !== latestRequestRef.current) return;
      let trialsData = [];
      if (res.data && res.data.data && res.data.meta) {
        trialsData = res.data.data;
        setMeta(res.data.meta);
      } else {
        trialsData = res.data || [];
        setMeta(null);
      }

      const finalTrials = showClosed ? trialsData : trialsData.filter(t => !t.closed);
      setTrials(finalTrials);

      // Notify parent of trials change
      if (onTrialsChange) {
        onTrialsChange(finalTrials);
      }
      if (onResultsChangeRef.current) {
        // eslint-disable-next-line no-unused-vars
        const { paginate, per_page, page: _page, ...query } = payload;
        onResultsChangeRef.current({ total: res.data?.meta?.total ?? finalTrials.length, query });
      }
    } catch (err) {
      if (requestId !== latestRequestRef.current) return;
      setError(err?.response?.data?.message || 'Failed to fetch trials');
      throw err; // Re-throw so parent can handle
    } finally {
      if (requestId === latestRequestRef.current) setLoading(false);
    }
  }, [page, showClosed, filters, sortBy, sortDirection, filteredUserIds, perPage, autoFilterPI, autoFilterFT, autoFilterSD, onTrialsChange, showSearchFilters, searchQueryKey, externalKey, priorityKey]);

  // Mirror the search filters in the URL (replace, so typing doesn't flood the history)
  useEffect(() => {
    if (!syncFiltersToUrl) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.keys(EMPTY_TRIAL_FILTERS).forEach(k => next.delete(k));
      Object.entries(JSON.parse(searchQueryKey)).forEach(([k, v]) => next.set(k, v));
      return next.toString() === prev.toString() ? prev : next;
    }, { replace: true });
  }, [syncFiltersToUrl, searchQueryKey, setSearchParams]);

  const updateSearchFilters = (patch) => {
    filtersTouched.current = true;
    setSearchFilters(f => ({ ...f, ...patch }));
    setPage(1);
  };

  const resetSearchFilters = () => {
    filtersTouched.current = true;
    setSearchFilters(EMPTY_TRIAL_FILTERS);
    setPage(1);
  };

  // The saved filters, when the user's settings arrive after the page opened (and nothing was changed yet)
  useEffect(() => {
    if (!rememberFiltersAs) return undefined;
    const apply = () => {
      if (filtersTouched.current) return;
      const saved = savedTrialFilters(rememberFiltersAs);
      if (!saved) return;
      if (syncFiltersToUrl && !urlHadFilters.current) setSearchFilters(saved.filters);
      setShowClosed(saved.closed);
    };
    window.addEventListener('userSettingsHydrated', apply);
    return () => window.removeEventListener('userSettingsHydrated', apply);
  }, [rememberFiltersAs, syncFiltersToUrl]);

  // Save them once the user has changed them (text after the debounce)
  useEffect(() => {
    if (!rememberFiltersAs || !filtersTouched.current) return;
    updateUserSettings({ [rememberFiltersAs]: { filters: JSON.parse(searchQueryKey), closed: showClosed } });
  }, [rememberFiltersAs, searchQueryKey, showClosed]);

  // Expose refresh function to parent - use useCallback to ensure stable reference
  useEffect(() => {
    if (onRefresh && typeof onRefresh === 'function') {
      onRefresh(fetchTrials);
    }
  }, [onRefresh, fetchTrials]);

  // Listen for refresh trigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchTrials();
    }
  }, [refreshTrigger, fetchTrials]);

  // Original useEffect for normal fetching
  useEffect(() => {
    fetchTrials();
  }, [fetchTrials]);

  // Column definitions
  const columnDefs = useMemo(() => {
    const toName = (val) => {
      if (val == null || val === '') return '';
      const fromUserObj = (u) => u?.name;

      if (typeof val === 'object') {
        const nm = fromUserObj(val);
        if (nm) return nm;
        const idCandidate = val.id ?? val.userId ?? val.userid;
        if (idCandidate !== null) {
          const u = usersById?.[String(idCandidate)] || (users || []).find(u => String(u.id) === String(idCandidate));
          return fromUserObj(u) || String(idCandidate);
        }
        return '';
      }

      const idStr = String(val);
      if (idStr === '0' || idStr.toLowerCase() === 'null' || idStr.toLowerCase() === 'undefined' || idStr.trim() === '') return '';
      const u = usersById?.[idStr] || (users || []).find(u => String(u.id) === idStr);
      if (u) return fromUserObj(u);
      return idStr;
    };

    const short = (s) => (typeof s === 'string' && s.length > 20 ? s.slice(0, 20) + '…' : s || '-');

    return [
      { key: 'protocolno', label: 'Protocol No', render: t => t.protocolno || '-' },
      { key: 'trialno', label: 'Trial No', render: t => t.trialno || '-' },
      { key: 'contract_no', label: CO_LABEL, render: t => t.contract_no ?? '-' },
      // Hidden by default (not in defaultColumns); enabled from the column menu
      { key: 'customer_no', label: 'Customer No', render: t => t.customer_no ?? '-' },
      { key: 'year', label: 'Year', render: t => t.year ?? '-' },
      { key: 'country', label: 'Country', render: t => t.country || '-' },
      { key: 'host', label: 'Host', render: t => short(t.host) },
      { key: 'crop', label: 'Crop', render: t => short(t.crop) },
      { key: 'study_type', label: 'Study Type', render: t => t.study_type || '-' },
      { key: 'location', label: 'Location', render: t => short(t.location) },
      { key: 'test_item', label: 'Test Item', render: t => short(t.test_item) },
      { key: 'customer', label: 'Customer', render: t => t.customer?.name || '-' },
      { key: 'studydirector', label: 'Study Director', render: t => { const n = toName(t.studydirector); return short(n); } },
      {
        key: 'PI',
        label: 'Principal Investigator',
        render: t => {
          const possible = t.PI ?? t.principal_investigator ?? t.principal_investigator_id ?? t.principalInvestigator ?? null;
          const n = toName(possible);
          return short(n);
        }
      },
      {
        key: 'FT',
        label: 'Field Technician',
        render: t => {
          const possible = t.FT ?? t.field_technician ?? t.field_technician_id ?? t.fieldTechnician ?? null;
          const n = toName(possible);
          return short(n);
        }
      },
      {
        key: 'status',
        label: 'Status',
        render: t => <TrialStatusBadge closed={t.closed} />
      }
    ];
  }, [users, usersById]);

  const visibleColumns = columnDefs.filter(c => columns.includes(c.key));

  function toggleColumn(key) {
    setColumns(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }

  async function saveColumns() {
    setSavingColumns(true);
    const cols = [...columns];
    sessionStorage.setItem(columnStorageKey, JSON.stringify(cols));
    const ok = await updateUserSettings({ [`${columnStorageKey}_value`]: cols });
    if (!ok) console.warn('Failed to persist column config');
    setSavingColumns(false);
    setShowColumnConfigDropdown(false);
    setColumns(cols);
  }// Close column dropdown on outside click or Escape
  useEffect(() => {
    if (!showColumnConfigDropdown) return;
    function handleClickAway(e) {
      if (!columnMenuRef.current) return;
      if (!columnMenuRef.current.contains(e.target)) {
        setShowColumnConfigDropdown(false);
      }
    }
    function handleKey(e) {
      if (e.key === 'Escape') {
        setShowColumnConfigDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('touchstart', handleClickAway);
    window.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('touchstart', handleClickAway);
      window.removeEventListener('keydown', handleKey);
    };
  }, [showColumnConfigDropdown]);

  // Handle trial row click
  const handleTrialClick = (trial) => {
    if (rowClickSelects && massUpdateMode) {
      handleSelectionChange(trial.id, !selectedTrialIds.includes(trial.id));
      return;
    }
    if (onTrialSelect) {
      onTrialSelect(trial);
    }
  };

  // "Select all on this page": checked when every row shown is selected
  const pageSelection = useMemo(() => {
    const selected = new Set(selectedTrialIds);
    const count = trials.filter(t => selected.has(t.id)).length;
    return { all: trials.length > 0 && count === trials.length, some: count > 0 };
  }, [trials, selectedTrialIds]);

  const togglePageSelection = (checked) => {
    if (!onSelectionChange) return;
    const pageIds = trials.map(t => t.id);
    onSelectionChange(checked
      ? [...new Set([...selectedTrialIds, ...pageIds])]
      : selectedTrialIds.filter(id => !pageIds.includes(id)));
  };

  // Handle mass update selection
  const handleSelectionChange = (trialId, checked) => {
    if (!onSelectionChange) return;

    let newSelection;
    if (checked) {
      newSelection = [...selectedTrialIds, trialId];
    } else {
      newSelection = selectedTrialIds.filter(id => id !== trialId);
    }
    onSelectionChange(newSelection);
  };

  return (
    <div className={`w-full flex flex-col ${className}`}>
      {/* Filters */}
      {showFilters && showSearchFilters && (
        <TrialSearchFilters
          filters={searchFilters}
          onChange={updateSearchFilters}
          onReset={resetSearchFilters}
          compact={compactSearchFilters}
        />
      )}
      {showFilters && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 flex-1">
            {showProtocolFilter && !autoFilterProtocol && !showSearchFilters && (
              <input
                type="text"
                placeholder="Filter by Protocol No"
                value={filters.protocolno}
                onChange={e => { setFilters(f => ({ ...f, protocolno: e.target.value })); setPage(1); }}
                className="form-input rounded border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
              />
            )}
            {showCustomerFilter && !autoFilterCustomer && !showSearchFilters && (
              <select
                value={filters.customer}
                onChange={e => { setFilters(f => ({ ...f, customer: e.target.value })); setPage(1); }}
                className="form-select rounded border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
              >
                <option value="">All Customers</option>
                {[...customers].sort((a, b) => a.name.localeCompare(b.name)).map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            )}
            {showStudyDirectorFilter && !autoFilterSD && !showSearchFilters && (
              <input
                type="text"
                placeholder="Type Study Director Name"
                value={studyDirectorInput}
                onChange={e => {
                  const val = e.target.value;
                  setStudyDirectorInput(val);
                  if (debounceTimeout) clearTimeout(debounceTimeout);
                  const timeout = setTimeout(() => {
                    if (val.trim() === '') {
                      setFilteredUserIds([]);
                    } else {
                      const filtered = users
                        .filter(u => u.name.toLowerCase().includes(val.trim().toLowerCase()))
                        .map(u => String(u.id));
                      setFilteredUserIds(filtered);
                    }
                    setPage(1);
                  }, 1000);
                  setDebounceTimeout(timeout);
                }}
                className="form-input rounded border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
                autoComplete="off"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            {showClosedToggle && (
              <label className="group relative inline-flex items-center gap-3">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={showClosed}
                  onChange={e => {
                    filtersTouched.current = true;
                    setShowClosed(e.target.checked);
                  }}
                />
                <span className="relative h-6 w-10 flex-none rounded-full bg-gray-300 transition-all duration-150 ease-out peer-checked:bg-emerald-500 peer-focus:ring-3 peer-focus:ring-emerald-500/50 peer-focus:ring-offset-2 peer-focus:ring-offset-white peer-disabled:cursor-not-allowed peer-disabled:opacity-75 before:absolute before:top-1 before:left-1 before:size-4 before:rounded-full before:bg-white before:transition-transform before:duration-150 before:ease-out before:content-[''] peer-checked:before:translate-x-full dark:bg-gray-700 dark:peer-checked:bg-emerald-500 dark:peer-focus:ring-offset-gray-900"></span>
                <span className="text-sm font-medium">Show closed trials</span>
              </label>
            )}
            {showColumnConfig && (
              <div className="relative" ref={columnMenuRef}>
                <button
                  type="button"
                  aria-label="Configure columns"
                  title="Configure columns"
                  onClick={() => setShowColumnConfigDropdown(v => !v)}
                  className="p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:text-gray-800 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M3 4h18" />
                    <path d="M6 10h12" />
                    <path d="M10 16h4" />
                  </svg>
                </button>
                {showColumnConfigDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-60 z-30 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Table Columns</span>
                      <button type="button" onClick={() => setShowColumnConfigDropdown(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" aria-label="Close">✕</button>
                    </div>
                    <div className="flex flex-col gap-1 max-h-56 overflow-auto pr-1">
                      {columnDefs.map(col => (
                        <label key={col.key} className="inline-flex items-center gap-2 px-1 py-1 rounded text-xs cursor-pointer">
                          <input type="checkbox" checked={columns.includes(col.key)} onChange={() => toggleColumn(col.key)} className="rounded" />
                          <span className="select-none">{col.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button type="button" className="px-3 py-1 rounded border text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200" disabled={savingColumns} onClick={() => setColumns(defaultColumns)}>Reset</button>
                      <button type="button" className="px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50" disabled={savingColumns} onClick={saveColumns}>{savingColumns ? 'Saving...' : 'Save'}</button>
                    </div>
                    <p className="mt-2 text-[10px] leading-snug text-gray-500 dark:text-gray-400">Preferences stored & synced.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loading/Error States */}
      <div className="flex-1 flex flex-col min-h-0">
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading trials...</div>
        ) : error ? (
          <div className="text-center py-8 text-red-600 dark:text-red-400">{error}</div>
        ) : (
          <>
            {/* Table */}
            <div className={`flex-1 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow ${tableClassName}`}>
              <table className="min-w-full align-middle text-sm whitespace-nowrap border-separate border-spacing-0">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700/50">
                    {massUpdateMode && (
                      <th className="bg-gray-100/75 px-3 py-4 text-center font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            aria-label="Select all trials on this page"
                            checked={pageSelection.all}
                            ref={el => { if (el) el.indeterminate = pageSelection.some && !pageSelection.all; }}
                            onChange={e => togglePageSelection(e.target.checked)}
                            disabled={trials.length === 0}
                          />
                          <span>Select</span>
                        </label>
                      </th>
                    )}
                    {visibleColumns.map(col => {
                      const clickable = SORTABLE_COLUMNS.includes(col.key);
                      const visibilityClass = col.key === 'protocolno' || col.key === 'studydirector' ? 'hidden md:table-cell' : (col.key === 'status' ? 'hidden lg:table-cell' : '');
                      return (
                        <th
                          key={col.key}
                          className={`bg-gray-100/75 px-3 py-4 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50 ${clickable ? 'cursor-pointer select-none' : ''} ${visibilityClass}`}
                          onClick={() => {
                            if (!clickable) return;
                            if (sortBy === col.key) {
                              setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortBy(col.key);
                              setSortDirection('asc');
                            }
                            setPage(1);
                          }}
                        >
                          {col.label} {sortBy === col.key && (<span className="ml-1 text-xs">{sortDirection === 'asc' ? '▲' : '▼'}</span>)}
                        </th>
                      );
                    })}
                    {showActions && !massUpdateMode && (
                      <th className="bg-gray-100/75 px-3 py-4 text-right font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {trials.length === 0 ? (
                    (() => {
                      const colCount = visibleColumns.length + (massUpdateMode ? 1 : 0) + (showActions && !massUpdateMode ? 1 : 0);
                      return <tr><td colSpan={colCount} className="p-8 text-center text-gray-500 dark:text-gray-400">No trials found.</td></tr>;
                    })()
                  ) : (
                    trials.map(trial => (
                      <tr
                        key={trial.id}
                        className={`border-b border-gray-100 dark:border-gray-700/50 ${onTrialSelect || (rowClickSelects && massUpdateMode) ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}`}
                        onClick={() => handleTrialClick(trial)}
                      >
                        {massUpdateMode && (
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedTrialIds.includes(trial.id)}
                              onChange={e => handleSelectionChange(trial.id, e.target.checked)}
                              onClick={e => e.stopPropagation()} // Prevent row click when clicking checkbox
                            />
                          </td>
                        )}
                        {visibleColumns.map(col => {
                          const visClass = col.key === 'protocolno' || col.key === 'studydirector' ? 'hidden md:table-cell' : (col.key === 'status' ? 'hidden lg:table-cell' : '');
                          return <td key={col.key} className={`px-3 py-2 ${visClass}`}>{col.render(trial)}</td>;
                        })}
                        {showActions && !massUpdateMode && (
                          <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-end gap-2">
                              <Link to={`/trials/${trial.id}`} state={backHere} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm leading-5 font-semibold text-gray-800 hover:border-gray-300 hover:text-gray-900 hover:shadow-xs focus:ring-3 focus:ring-gray-300/25 active:border-gray-200 active:shadow-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:text-gray-200 dark:focus:ring-gray-600/40 dark:active:border-gray-700">
                                View
                              </Link>
                              {currentUserId && (
                                <Link to={`/trials/${trial.id}/edit`} state={backHere} className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-sm leading-5 font-semibold text-emerald-800 hover:border-emerald-300 hover:text-emerald-900 hover:shadow-xs focus:ring-3 focus:ring-emerald-300/25 active:border-emerald-200 active:shadow-none dark:border-emerald-700 dark:bg-emerald-900 dark:text-emerald-200 dark:hover:border-emerald-600 dark:hover:text-emerald-200 dark:focus:ring-emerald-600/40 dark:active:border-emerald-700">
                                  Edit
                                </Link>
                              )}
                              {currentUserId && (
                                <button
                                  onClick={() => {
                                    navigate(`/trials/new`, {
                                      state: { trialId: trial.id }
                                    });
                                  }}
                                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-sm leading-5 font-semibold text-blue-800 hover:border-blue-300 hover:text-blue-900 hover:shadow-xs focus:ring-3 focus:ring-blue-300/25 active:border-blue-200 active:shadow-none dark:border-blue-700 dark:bg-blue-900 dark:text-blue-200 dark:hover:border-blue-600 dark:hover:text-blue-200 dark:focus:ring-blue-600/40 dark:active:border-blue-700"
                                >
                                  Copy
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-2 flex-shrink-0">
              <div className="text-xs text-gray-600 dark:text-gray-300">
                {meta ? (
                  <>
                    Page {meta.current_page} of {meta.last_page} · Showing {Array.isArray(trials) ? trials.length : 0} of {meta.total} trials
                  </>
                ) : (
                  <>
                    {perPage === 'all' ? `Showing ${Array.isArray(trials) ? trials.length : 0} trials` : ''}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 dark:text-gray-300">Rows per page</label>
                <select
                  className="form-select rounded border-gray-300 px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-800"
                  value={String(perPage)}
                  onChange={e => {
                    const val = e.target.value === 'all' ? 'all' : Number(e.target.value);
                    setPerPage(val);
                    setPage(1);
                  }}
                >
                  {[10, 15, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                  <option value="all">All</option>
                </select>
                {meta && meta.last_page > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                      disabled={page === 1}
                      onClick={() => setPage(Math.max(1, page - 1))}
                    >
                      Prev
                    </button>
                    <button
                      className="px-2 py-1 text-xs border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
                      disabled={page >= meta.last_page}
                      onClick={() => setPage(Math.min(meta.last_page, page + 1))}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}